import { FlatInfoComponent } from '@components/shared/flat-info/flat-info.component';
import { FlatInfo } from '@models/flat-info.model';
import { TranslateModule } from '@ngx-translate/core';
import { API_ENDPOINTS, ApiService } from '@services/api/api.service';
import { FlatClientService } from '@services/api/flat/flat-client.service';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { FlatInfoService } from '@services/util/flat-info.service';
import { LogService } from '@services/util/log.service';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal } from '@angular/core';
import { FlatIssuesTableComponent } from '../flat-issues-table/flat-issues-table.component';

@Component({
  selector: 'app-booking-room-display',
  standalone: true,
  imports: [CommonModule, TranslateModule, FlatInfoComponent, FlatIssuesTableComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="surface-card border-round shadow-2 mb-4 p-4">
      <!-- Room Header -->
      <div class="mb-4 flex items-center justify-between">
        <h3 class="m-0 text-lg font-semibold">
          {{ 'booking.detail.room.title' | translate }}
        </h3>
        @if (flatInfo()) {
          <app-flat-info
            [flatInfo]="flatInfo()!"
            style="badge"
          >
          </app-flat-info>
        }
      </div>

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <!-- Floorplan Section -->
        <div class="col-span-1">
          <h4 class="text-md mb-3 font-medium">
            {{ 'booking.detail.room.floorplan' | translate }}
          </h4>
          @if (isLoading()) {
            <div class="flex min-h-64 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div class="text-center text-gray-500">
                <i class="pi pi-spin pi-spinner mb-2 text-2xl"></i>
                <p>{{ 'common.loading' | translate }}</p>
              </div>
            </div>
          } @else if (floorplanUrl() && !floorplanError()) {
            <div class="flex min-h-64 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <img
                [src]="floorplanUrl()"
                [alt]="'Floorplan for ' + (flat.name || 'room')"
                class="max-h-64 max-w-full object-contain"
                (error)="onImageError()"
              />
            </div>
          } @else {
            <div class="flex min-h-64 items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-4">
              <div class="text-center text-gray-500">
                <i class="pi pi-image mb-2 text-4xl"></i>
                <p>{{ 'booking.detail.room.floorplan.notAvailable' | translate }}</p>
              </div>
            </div>
          }
        </div>

        <!-- Room Details Section -->
        <div class="col-span-1">
          <h4 class="text-md mb-3 font-medium">
            {{ 'booking.detail.room.details' | translate }}
          </h4>
          <div class="space-y-3">
            <!-- Room Number -->
            @if (currentFlat().flat_number || currentFlat().name) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.number' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().flat_number || currentFlat().name }}</span>
              </div>
            }

            <!-- Floor -->
            @if (currentFlat().floor?.name) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.floor' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().floor!.name }}</span>
              </div>
            }

            <!-- Room Type -->
            @if (currentFlat().flatType?.name) {
              <div class="flex justify-between">
                <span class="text-gray-600  dark:text-gray-400">{{ 'booking.detail.room.type' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().flatType!.name }}</span>
              </div>
            }

            <!-- Number of Rooms -->
            @if (currentFlat().number_of_rooms) {
              <div class="flex justify-between">
                <span class="text-gray-600  dark:text-gray-400">{{ 'booking.detail.room.roomCount' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().number_of_rooms }}</span>
              </div>
            }

            <!-- Bedrooms -->
            @if (currentFlat().number_of_bedrooms !== undefined && currentFlat().number_of_bedrooms !== null) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.bedrooms' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().number_of_bedrooms }}</span>
              </div>
            }

            <!-- Bathrooms -->
            @if (currentFlat().number_of_bathrooms !== undefined && currentFlat().number_of_bathrooms !== null) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.bathrooms' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().number_of_bathrooms }}</span>
              </div>
            }

            <!-- Bed Configuration -->
            @if (hasBedInfo()) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.beds' | translate }}:</span>
                <span class="font-medium">{{ getBedConfiguration() }}</span>
              </div>
            }

            <!-- Rental Status -->
            @if (currentFlat().for_rent !== undefined) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.forRent' | translate }}:</span>
                <span class="font-medium">
                  @if (currentFlat().for_rent) {
                    <span class="text-green-600">{{ 'common.yes' | translate }}</span>
                  } @else {
                    <span class="text-red-600">{{ 'common.no' | translate }}</span>
                  }
                </span>
              </div>
            }

            <!-- Occupancy -->
            @if (currentFlat().occupancy) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.occupancy' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().occupancy }}</span>
              </div>
            }

            <!-- Size -->
            @if (currentFlat().size) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.size' | translate }}:</span>
                <span class="font-medium">{{ currentFlat().size }}</span>
              </div>
            }

            <!-- Balcony -->
            @if (currentFlat().balcony !== undefined) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.amenities.balcony' | translate }}:</span>
                <span class="font-medium">
                  @if (currentFlat().balcony) {
                    <span class="text-green-600">{{ 'common.yes' | translate }}</span>
                  } @else {
                    <span class="text-red-600">{{ 'common.no' | translate }}</span>
                  }
                </span>
              </div>
            }

            <!-- Terrace -->
            @if (currentFlat().terrace !== undefined) {
              <div class="flex justify-between">
                <span class="text-gray-600 dark:text-gray-400">{{ 'booking.detail.room.amenities.terrace' | translate }}:</span>
                <span class="font-medium">
                  @if (currentFlat().terrace) {
                    <span class="text-green-600">{{ 'common.yes' | translate }}</span>
                  } @else {
                    <span class="text-red-600">{{ 'common.no' | translate }}</span>
                  }
                </span>
              </div>
            }
          </div>
        </div>
      </div>

      <!-- Issues Section -->
      <div class="mt-6 border-t border-gray-200 pt-4">
        <h4 class="text-md mb-3 font-medium">
          {{ 'booking.detail.room.issues' | translate }}
        </h4>
        @if (currentFlat()) {
          <app-flat-issues-table [flat]="currentFlat()"></app-flat-issues-table>
        }
      </div>
    </div>
  `
})
export class BookingRoomDisplayComponent implements OnInit {
  @Input() flat!: IFlatModel;

  private readonly flatInfoService = inject(FlatInfoService);
  private readonly flatClientService = inject(FlatClientService);
  private readonly apiService = inject(ApiService);
  private readonly logService = inject(LogService);

  protected readonly flatInfo = signal<FlatInfo | null>(null);
  protected readonly fullFlat = signal<IFlatModel | null>(null);
  protected readonly floorplanUrl = signal<string | null>(null);
  protected readonly floorplanError = signal<boolean>(false);
  protected readonly isLoading = signal<boolean>(false);

  ngOnInit(): void {
    this.logService.debug('BookingRoomDisplayComponent - Input flat:', this.flat);
    this.loadFlatData();
  }

  /**
   * Initialize flat info for display
   */
  private loadFlatData(): void {
    this.isLoading.set(true);
    this.flatClientService.getItem(this.flat.id).subscribe({
      next: (flat: IFlatModel) => {
        this.logService.debug('BookingRoomDisplayComponent - Loaded flat data:', flat);
        this.logService.debug('BookingRoomDisplayComponent - Flat fields check:', {
          number_of_bedrooms: flat.number_of_bedrooms,
          number_of_bathrooms: flat.number_of_bathrooms,
          double_bed: flat.double_bed,
          single_bed: flat.single_bed,
          extra_bed: flat.extra_bed,
          for_rent: flat.for_rent
        });
        this.fullFlat.set(flat);
        if (flat.blueprint) {
          this.floorplanUrl.set(`${this.apiService.getBaseUrl()}${API_ENDPOINTS.blueprint}${flat.blueprint}`);
        }
        this.isLoading.set(false);
      },
      error: (error: any) => {
        this.logService.error('Error loading flat data:', error);
        this.isLoading.set(false);
      }
    });
    try {
      const flatInfoData = this.flatInfoService.createFlatInfo(this.flat);
      this.flatInfo.set(flatInfoData);
    } catch (error) {
      this.logService.error('Error creating flat info:', error);
      // Fallback flat info
      this.flatInfo.set(
        new FlatInfo(
          this.flat.id || 0,
          this.flat.building?.name || 'Unknown Building',
          this.flat.name || 'Unknown Room',
          'bg-gray-100 text-gray-800',
          this.flat.floor?.name || ''
        )
      );
    }
  }

  /**
   * Handle image loading errors
   */
  protected onImageError(): void {
    this.floorplanError.set(true);
  }

  /**
   * Check if flat has any amenities to display
   */
  /**
   * Get current flat data (loaded from API if available, fallback to input)
   */
  protected currentFlat(): IFlatModel {
    return this.fullFlat() || this.flat;
  }

  /**
   * Check if flat has bed information to display
   */
  protected hasBedInfo(): boolean {
    const flat = this.currentFlat();
    return !!(flat.double_bed || flat.single_bed || flat.extra_bed);
  }

  /**
   * Get formatted bed configuration string
   */
  protected getBedConfiguration(): string {
    const flat = this.currentFlat();
    const beds: string[] = [];

    if (flat.double_bed) {
      beds.push(`${flat.double_bed} double`);
    }
    if (flat.single_bed) {
      beds.push(`${flat.single_bed} single`);
    }
    if (flat.extra_bed) {
      beds.push(`${flat.extra_bed} extra`);
    }

    return beds.join(', ');
  }

  /**
   * Get debug bed configuration string showing all values
   */
  protected getBedConfigurationDebug(): string {
    const flat = this.currentFlat();
    return `Double: ${flat.double_bed || 0}, Single: ${flat.single_bed || 0}, Extra: ${flat.extra_bed || 0}`;
  }
}
